// src/components/ProjectManagement/NotificationBell.jsx
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot, updateDoc, doc, orderBy, limit, startAfter, getDocs } from 'firebase/firestore';

const NotificationBell = ({ userId, userRole }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [pageSize] = useState(20);

  // Fetch initial notifications
  useEffect(() => {
    if (!userId) return;
    
    const q = query(
      collection(db, 'notifications'), 
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
      setNotifications(notifs);
      setUnreadCount(notifs.filter(n => !n.read).length);
      setLoading(false);
      
      if (snapshot.docs.length > 0) {
        setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
        setHasMore(snapshot.docs.length === pageSize);
      } else {
        setHasMore(false);
      }
    });
    
    return unsubscribe;
  }, [userId, pageSize]);

  // Load more notifications
  const loadMore = async () => {
    if (!lastDoc || loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        limit(pageSize)
      );
      
      const snapshot = await getDocs(q);
      const moreNotifs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date()
      }));
      
      setNotifications(prev => [...prev, ...moreNotifs]);
      setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === pageSize);
    } catch (error) {
      console.error('Error loading more notifications:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const markAsRead = async (id) => {
    await updateDoc(doc(db, 'notifications', id), { 
      read: true, 
      readAt: new Date() 
    });
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = async () => {
    const unreadNotifs = notifications.filter(n => !n.read);
    for (const notif of unreadNotifs) {
      await updateDoc(doc(db, 'notifications', notif.id), { 
        read: true, 
        readAt: new Date() 
      });
    }
    setUnreadCount(0);
  };

  const clearAllNotifications = async () => {
    try {
      const allNotifs = [...notifications];
      for (const notif of allNotifs) {
        await updateDoc(doc(db, 'notifications', notif.id), { 
          deleted: true,
          deletedAt: new Date()
        });
      }
      setNotifications([]);
      setUnreadCount(0);
      setHasMore(false);
      setShowClearConfirm(false);
    } catch (error) {
      console.error('Error clearing notifications:', error);
      alert('Failed to clear notifications');
    }
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'task_assigned': return '📋';
      case 'task_started': return '🚀';
      case 'task_completed': return '✅';
      case 'task_rejected': return '❌';
      case 'task_verified': return '✓';
      case 'deadline_warning': return '⚠️';
      case 'time_extended': return '⏰';
      default: return '🔔';
    }
  };

  const getNotificationColor = (type) => {
    switch(type) {
      case 'task_assigned': return '#3b82f6';
      case 'task_started': return '#f59e0b';
      case 'task_completed': return '#10b981';
      case 'task_rejected': return '#ef4444';
      case 'task_verified': return '#10b981';
      case 'deadline_warning': return '#ef4444';
      case 'time_extended': return '#8b5cf6';
      default: return '#6b7280';
    }
  };

  const formatTime = (date) => {
    if (!date) return '';
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} min ago`;
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    if (days < 7) return `${days} day${days !== 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      <div className="notification-wrapper">
        <button className="bell-icon" onClick={() => setShowModal(true)}>
          🔔
          {unreadCount > 0 && <span className="bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
        </button>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="notification-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notification-modal-header">
              <div className="header-left">
                <span className="header-icon">🔔</span>
                <h3>Notifications</h3>
                {unreadCount > 0 && <span className="unread-badge">{unreadCount} unread</span>}
              </div>
              <div className="header-actions">
                {unreadCount > 0 && (
                  <button onClick={markAllAsRead} className="mark-all-btn">
                    Mark all read
                  </button>
                )}
                {notifications.length > 0 && (
                  <button onClick={() => setShowClearConfirm(true)} className="clear-all-btn">
                    Clear all
                  </button>
                )}
                <button onClick={() => setShowModal(false)} className="close-modal-btn">
                  ✕
                </button>
              </div>
            </div>
            
            <div className="notification-modal-body">
              {loading ? (
                <div className="notifications-loading">
                  <div className="loading-spinner-small"></div>
                  <p>Loading notifications...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="no-notifications">
                  <div className="no-notif-icon">📭</div>
                  <p>No notifications yet</p>
                  <small>When you get notifications, they'll appear here</small>
                </div>
              ) : (
                <>
                  <div className="notifications-list-modal">
                    {notifications.map(notif => (
                      <div 
                        key={notif.id} 
                        className={`notification-item-modal ${!notif.read ? 'unread' : ''}`}
                        onClick={() => markAsRead(notif.id)}
                      >
                        <div 
                          className="notification-icon-modal" 
                          style={{ background: `${getNotificationColor(notif.type)}15` }}
                        >
                          <span style={{ color: getNotificationColor(notif.type) }}>
                            {getNotificationIcon(notif.type)}
                          </span>
                        </div>
                        <div className="notification-content-modal">
                          <div className="notification-title">
                            <strong>{notif.title}</strong>
                            {!notif.read && <span className="new-dot">New</span>}
                          </div>
                          <p className="notification-message">{notif.message}</p>
                          {notif.assignmentNote && (
                            <div className="notification-note">
                              💬 "{notif.assignmentNote}"
                            </div>
                          )}
                          <div className="notification-time">
                            {formatTime(notif.createdAt)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {hasMore && (
                    <div className="load-more-container">
                      <button 
                        onClick={loadMore} 
                        className="load-more-btn"
                        disabled={loadingMore}
                      >
                        {loadingMore ? 'Loading...' : 'Load more notifications'}
                      </button>
                    </div>
                  )}
                  
                  <div className="notification-footer-info">
                    <span>Showing {notifications.length} notifications</span>
                    {hasMore && <span> • Scroll for more</span>}
                  </div>
                </>
              )}
            </div>
            
            <div className="notification-modal-footer">
              <button onClick={() => setShowModal(false)} className="close-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Clear All Confirmation Modal */}
      {showClearConfirm && (
        <div className="modal-overlay" onClick={() => setShowClearConfirm(false)}>
          <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirmation-modal-header">
              <span className="confirmation-icon">⚠️</span>
              <h3>Clear All Notifications?</h3>
            </div>
            <div className="confirmation-modal-body">
              <p>This action cannot be undone. All notifications will be permanently deleted.</p>
            </div>
            <div className="confirmation-modal-actions">
              <button onClick={clearAllNotifications} className="confirm-clear-btn">
                Yes, Clear All
              </button>
              <button onClick={() => setShowClearConfirm(false)} className="cancel-clear-btn">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationBell;